/* eslint-disable react/jsx-no-useless-fragment */
import * as React from 'react';
import { LayoutProps, ButtonGroup, TextButtonProps } from '@oneaudi/unified-web-components';
import { UeContainer, UeReference } from '@oneaudi/falcon-tools';
import { CtaCallbackButton, CtaCallbackButtonProps } from './CtaCallbackButton';
import { CtaUrlButton, CtaUrlButtonProps } from './CtaUrlButton';
import { ctaBarButtonConfigProps } from './pbc-types';
import searchStateContext from '../../../contexts/map-context';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';

export type CtaBarProps = LayoutProps & {
  ctaBarButtonConfig: ctaBarButtonConfigProps[];
  displayname: string;
  partnerId: string;
  favoritePartner: string | undefined;
};

export type CtaButtonProps = {
  displayname: string;
  label: string;
  position: string;
  variant: TextButtonProps['variant'];
};

export const CtaBar = ({
  ctaBarButtonConfig,
  displayname,
  partnerId,
  favoritePartner,
}: CtaBarProps): React.ReactElement | null => {
  const [ctaButtons, setCtaButtons] = React.useState<(JSX.Element | undefined)[]>();
  const dispatch = searchStateContext.useMapDispatch();

  const { showModal } = searchStateContext.useMapState();

  React.useEffect(() => {
    const ctaBtns = ctaBarButtonConfig.map((buttonConfig, index) => {
      const { label, variant, url, clickHandler } = buttonConfig;
      const position = (index + 1).toString();
      const setShowModal = () => {
        dispatch({ type: ActionTypes.setShowModal, payload: !showModal });
      };

      if (url) {
        if (clickHandler && url) {
          // eslint-disable-next-line no-console
          console.debug(
            'CTA has both clickHandler and url configured. Url is prefered and will be used!',
            buttonConfig,
          );
        }

        const ctaUrlButtonProps: CtaUrlButtonProps = {
          displayname,
          label,
          variant,
          url,
          position,
        };

        return (
          <UeReference propertyPath={`ctaBarButtonConfig.${index}`}>
            <CtaUrlButton key={position} {...ctaUrlButtonProps} />
          </UeReference>
        );
      }

      if (clickHandler) {
        const cleanPartnerId = partnerId.replace(/\D/g, '');
        const isSetPartner = !favoritePartner || favoritePartner !== cleanPartnerId;
        const ctaCallbackButtonProps: CtaCallbackButtonProps = {
          displayname,
          label,
          variant,
          clickHandler,
          setShowModal,
          position,
          isSetPartner,
        };

        return <CtaCallbackButton key={position} {...ctaCallbackButtonProps} />;
      }

      // eslint-disable-next-line no-console
      console.debug('CTA config is missing url or clickHandler!', buttonConfig);
      return undefined;
    });

    setCtaButtons(ctaBtns);
  }, [ctaBarButtonConfig, partnerId, favoritePartner]);

  return ctaBarButtonConfig.length > 0 ? (
    <UeContainer propertyPath="ctaBarButtonConfig" label="CTA Bar Button Config">
      {ctaButtons && ctaButtons?.length > 1 ? (
        <ButtonGroup margin={['24px', 0, '24px', 0]} orientation="vertical" data-testid="cta-group">
          {ctaButtons as any}
        </ButtonGroup>
      ) : (
        ctaButtons
      )}
    </UeContainer>
  ) : null;
};
